// In-house Web Push (W3C Push API + VAPID) client glue.
//
// The flow:
//   1. Settings UI calls `subscribe(userId)` from a user gesture.
//   2. We register `/sw.js`, ask the browser for a `PushSubscription` keyed
//      to our VAPID public key, and persist the endpoint + keys to
//      `push_subscriptions` via the `register_push_subscription` RPC. The
//      RPC dedupes by endpoint (see migration for the shared-browser
//      reasoning).
//   3. The `notify` edge function later fans out to these endpoints using
//      the matching VAPID private key.
//
// Browser support gate is intentionally permissive — `getSubscriptionState`
// surfaces a 4-state enum that the UI uses to decide between "show toggle",
// "show denied hint", and "hide entirely". The `usePushNotifications()`
// feature flag still gates whether we offer the UI at all.

import { client } from '../services/api/client';
import { isSupabaseError } from './logger';

// VAPID public key — generated via `npx web-push generate-vapid-keys`. The
// browser verifies pushes against it; hardcoding is fine because the value
// is public by design. The matching private key lives in the
// `VAPID_PRIVATE` edge function secret.
const VAPID_PUBLIC_KEY = 'BMkpwMG02docT3xeASiYxwYzEw3BLF-MEzWWptRezYxrjtTa-TjZiO9fB20ASCLrx5Wv6GLzmM0IhR1CWgwpLCU';

const SW_URL = '/sw.js';

export type SubscriptionState = 'subscribed' | 'unsubscribed' | 'unsupported' | 'denied';

export type SubscribeStep =
    | 'service-worker-register'
    | 'service-worker-ready'
    | 'get-subscription'
    | 'push-subscribe'
    | 'persist';

// Browsers throw the same generic error (e.g. Chrome's `AbortError:
// Registration failed - push service error`) from several different places.
// Tagging which step failed, plus the original error name, is what lets us
// tell "push service unreachable" apart from "RPC rejected the row" in Sentry.
export class PushSubscribeError extends Error {
    readonly step: SubscribeStep;
    readonly causeName: string;
    readonly cause: unknown;

    constructor(step: SubscribeStep, cause: unknown) {
        const causeName = describeCauseName(cause);
        super(`Push opt-in failed at "${step}": ${causeName}: ${describeCauseMessage(cause)}`);
        this.name = 'PushSubscribeError';
        this.cause = cause;
        this.step = step;
        this.causeName = causeName;
    }
}

function describeCauseName(cause: unknown): string {
    if (cause instanceof Error) {
        return cause.name;
    } else if (isSupabaseError(cause)) {
        return cause.code;
    } else {
        return 'UnknownError';
    }
}

function describeCauseMessage(cause: unknown): string {
    if (cause instanceof Error || isSupabaseError(cause)) {
        return cause.message;
    } else {
        return String(cause);
    }
}

async function runStep<T>(step: SubscribeStep, fn: () => Promise<T>): Promise<T> {
    try {
        return await fn();
    } catch (error) {
        throw new PushSubscribeError(step, error);
    }
}

export function isWebPushSupported(): boolean {
    return typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;
}

// VAPID public keys are URL-safe base64 without padding. `pushManager.subscribe`
// needs raw bytes, so we restore standard base64 then decode to a Uint8Array.
export function urlBase64ToUint8Array(base64: string): Uint8Array {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(normalized);
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) {
        bytes[i] = raw.charCodeAt(i);
    }
    return bytes;
}

// Clear any legacy push subscription whose `applicationServerKey` doesn't
// match our VAPID public key. Covers the OneSignal → in-house migration:
// both SWs share scope `/`, so registering `/sw.js` updates the same
// ServiceWorkerRegistration in place, leaving the OneSignal-keyed
// PushSubscription attached. That subscription will never receive our
// pushes (different server key, and no `push_subscriptions` row), but
// `getSubscriptionState()` still reports `'subscribed'` and the Settings
// UI hides the re-opt-in prompt. Idempotent for fresh in-house subs.
export async function clearMismatchedSubscription(): Promise<void> {
    if (!isWebPushSupported()) {
        return;
    }

    const registration = await navigator.serviceWorker.getRegistration('/');
    if (registration == null) {
        return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (subscription == null) {
        return;
    }

    const expected = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    const actual = subscription.options.applicationServerKey;
    if (actual != null && applicationServerKeyEquals(actual, expected)) {
        return;
    }

    await subscription.unsubscribe();
}

function applicationServerKeyEquals(actual: ArrayBuffer, expected: Uint8Array): boolean {
    const actualBytes = new Uint8Array(actual);
    if (actualBytes.length !== expected.length) {
        return false;
    }
    for (let i = 0; i < actualBytes.length; i += 1) {
        if (actualBytes[i] !== expected[i]) {
            return false;
        }
    }
    return true;
}

export async function getSubscriptionState(): Promise<SubscriptionState> {
    if (!isWebPushSupported()) {
        return 'unsupported';
    }

    if (Notification.permission === 'denied') {
        return 'denied';
    }

    const registration = await navigator.serviceWorker.getRegistration(SW_URL);
    if (registration == null) {
        return 'unsubscribed';
    }

    const subscription = await registration.pushManager.getSubscription();
    return subscription != null ? 'subscribed' : 'unsubscribed';
}

export async function subscribe(userId: string): Promise<void> {
    if (!isWebPushSupported()) {
        throw new Error('Web push is not supported in this browser');
    }

    if (userId == null || userId === '') {
        throw new Error('subscribe requires an authenticated user id');
    }

    if (!VAPID_PUBLIC_KEY) {
        throw new Error('VAPID public key is not configured');
    }

    const registration = await runStep('service-worker-register', () => navigator.serviceWorker.register(SW_URL));
    // `pushManager.subscribe` requires an active SW. `register` resolves as
    // soon as the registration object exists, before activation completes.
    await runStep('service-worker-ready', () => navigator.serviceWorker.ready);

    // If a subscription already exists (page reload, prior session), keep it
    // and just make sure the row is on the server. The RPC dedupes by
    // endpoint, so this is safe to re-run.
    const existing = await runStep('get-subscription', () => registration.pushManager.getSubscription());
    if (existing != null) {
        await runStep('persist', () => persist(existing));
        return;
    }

    const subscription = await runStep('push-subscribe', () => registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    }));
    await runStep('persist', () => persist(subscription));
}

export async function unsubscribe(): Promise<void> {
    if (!isWebPushSupported()) {
        return;
    }

    const registration = await navigator.serviceWorker.getRegistration(SW_URL);
    if (registration == null) {
        return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (subscription == null) {
        return;
    }

    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();

    const { error } = await client
        .from('push_subscriptions')
        .delete()
        .eq('endpoint', endpoint);
    if (error) {
        throw error;
    }
}

async function persist(subscription: PushSubscription): Promise<void> {
    // `toJSON()` gives us the URL-safe base64 string form the `web-push`
    // library on the server expects directly — no manual key encoding.
    const json = subscription.toJSON();
    if (json.endpoint == null || json.keys?.p256dh == null || json.keys?.auth == null) {
        throw new Error('PushSubscription is missing endpoint or keys');
    }

    const { error } = await client.rpc('register_push_subscription', {
        p_endpoint: json.endpoint,
        p_p256dh: json.keys.p256dh,
        p_auth: json.keys.auth,
        p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });

    if (error) {
        throw error;
    }
}
